/** 

=========================================================
* Vision UI PRO React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Visionware.

*/

import type { MouseEvent, MouseEventHandler, ReactNode } from "react";
import { Link as RouterLink, useHistory } from "react-router-dom";

// @mui material components
import Collapse from "@mui/material/Collapse";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Icon from "@mui/material/Icon";

// Vision UI Dashboard PRO React components
import VuiBox from "components/VuiBox";
import VuiButton from "components/VuiButton";

// import { useHistory } from 'react-router-dom';

import { useReloadNow } from "context/app";

// Custom styles for the SidenavCollapse
import {
  collapseItem,
  collapseIconBox,
  collapseIcon,
  collapseText,
  collapseArrow,
} from "examples/Sidenav/styles/sidenavCollapse";

// Vision UI Dashboard PRO React context
import { useVisionUIController } from "context";

// Models
import type { SidenavColor } from "models/theme.model";

interface SidenavCollapseProps {
  color?: SidenavColor;
  // An icon name, or the icon itself.
  icon: ReactNode;
  name: string;
  // The section's items.
  children?: ReactNode;
  active?: boolean;
  noCollapse?: boolean;
  open?: boolean;
  // Always open, without the arrow.
  allwaysShow?: boolean;
  // Makes the title a button to this route.
  mainLink?: string;
  onClick?: MouseEventHandler<HTMLDivElement>;
}

function SidenavCollapse({ color = "info", icon, name, children, active = false, noCollapse = false, open = false, allwaysShow, mainLink, ...rest }: SidenavCollapseProps) {
  const [controller] = useVisionUIController();
  const { miniSidenav, transparentSidenav } = controller;

  const history = useHistory();
  const reloadNow = useReloadNow();

  const bigButtonHandler = (e: MouseEvent, route: string) => {
    e.stopPropagation();
    history.push(route);
  }

  return (
    <>
      <ListItem component="li" className="menu-main-item">
        <VuiBox {...rest} sx={(theme) => collapseItem(theme, { active, transparentSidenav })} style={{ margin: 0 }}>
          {mainLink ? (
            <VuiButton
              // component={RouterLink}
              // to={mainLink}
              onClick={(e: MouseEvent) => {
                reloadNow();
                bigButtonHandler(e, mainLink);
              }}
              className="menu-main-btn"
              variant="text"
            >
              <ListItemIcon
                sx={(theme) => collapseIconBox(theme, { active, transparentSidenav, color })}
              >
                {typeof icon === "string" ? (
                  <Icon sx={(theme) => collapseIcon(theme, { active })}>{icon}</Icon>
                ) : (
                  icon
                )}
              </ListItemIcon>

              <ListItemText

                primary={name}
                sx={(theme) => collapseText(theme, { miniSidenav, transparentSidenav, active })}
              />
            </VuiButton>
          ) : (
            <>
              <ListItemIcon
                sx={(theme) => collapseIconBox(theme, { active, transparentSidenav, color })}
              >
                {typeof icon === "string" ? (
                  <Icon sx={(theme) => collapseIcon(theme, { active })}>{icon}</Icon>
                ) : (
                  icon
                )}
              </ListItemIcon>

              <ListItemText

                primary={name}
                sx={(theme) => collapseText(theme, { miniSidenav, transparentSidenav, active })}
              />
            </>
          )}
          {allwaysShow !== true && (
            <Icon
              sx={(theme) =>
                collapseArrow(theme, { noCollapse, transparentSidenav, miniSidenav, open })
              }
            >
              expand_less
            </Icon>
          )}
        </VuiBox>
      </ListItem>
      {children && (
        <Collapse in={open} unmountOnExit>
          {children}
        </Collapse>
      )}
    </>
  );
}

export default SidenavCollapse;
